import { Box, Typography, Paper, useTheme, CircularProgress } from "@mui/material";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Corporate } from "../../db_access/corporate";
import { Structure } from "../../db_access/structure";
import { Supply_chain } from "../../db_access/supply_chain";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { M3Button } from "../../components/m3";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";

const AIInsightCard = ({ type, title, description }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const dotColor = {
    critical: "#f43f5e", // red
    warning: "#f59e0b", // orange
    info: "#10b981",    // green
  }[type] || "#10b981";

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: "16px",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
      }}
    >
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: dotColor, mt: 1, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: "left" }}>{title}</Typography>
        <Typography variant="body2" sx={{ color: "textSecondary", mt: 0.5, fontSize: "14px", textAlign: "left", lineHeight: 1.6 }}>{description}</Typography>
      </Box>
    </Box>
  );
};

const OrganizationInsight = ({ ai_insight, organizationType }) => {
  const { selectedSupplyChainId, selectedCorporateId, selectedStructureId, refresh, setRefresh } = useContextProvider();
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [elapsed, setElapsed] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const organization = useMemo(() => {
    switch (organizationType) {
      case Supply_chain.label:
        return Supply_chain;
      case Structure.label:
        return Structure;
      case Corporate.label:
        return Corporate;
      default:
        return null;
    }
  }, [organizationType]);

  const selectedOrganizationId = useMemo(() => {
    switch (organizationType) {
      case Supply_chain.label:
        return selectedSupplyChainId;
      case Structure.label:
        return selectedStructureId;
      case Corporate.label:
        return selectedCorporateId;
      default:
        return null;
    }
  }, [organizationType, selectedSupplyChainId, selectedCorporateId, selectedStructureId]);

  const timerRef = useRef(null);
  const startTimer = () => {
    setElapsed(0);
    setIsGenerating(true);
    const start = Date.now();
    timerRef.current = setInterval(() => {
      setElapsed(Date.now() - start);
    }, 10);
  };
  const endTimer = () => {
    clearInterval(timerRef.current);
    setIsGenerating(false);
  };

  const handleGenerate = () => {
    if (!selectedOrganizationId || isGenerating) return;
    startTimer();
    organization.generate_insight_gemini_flash(selectedOrganizationId).finally(() => {
      setRefresh((p) => !p);
      endTimer();
    });
  };

  return (
    <Paper
      variant="Standard Container"
      sx={{
        p: 2.5,
        height: "100%",
        borderRadius: ORG_PALETTE.radius.card,
        border: "1px solid",
        borderColor: tok.border.card,
        bgcolor: tok.bg.card
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 4, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ p: 1, borderRadius: "10px", bgcolor: "#ecfdf5", color: "#10b981", display: "flex" }}>
            <Typography sx={{ fontSize: "20px" }}>💡</Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 500, pb: 1, fontSize: "20px", borderBottom: `1px solid ${tok.border.divider}` }}>
            {t("Intelligence Insights")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "textSecondary", mr: 1 }}>
            {isGenerating ? `Generating... ${(elapsed / 1000).toFixed(1)}s` : elapsed > 0 ? `Generated in ${(elapsed / 1000).toFixed(1)}s` : ""}
          </Typography>

          <M3Button
            variant="filled"
            size="md"
            icon={<AutoAwesomeOutlinedIcon style={{ fontSize: "18px" }} />}
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? t("Generating...") : t("Generate Insights")}
          </M3Button>
        </Box>
      </Box>

      <Box>
        {isGenerating ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4, flexDirection: "column", gap: 2 }}>
            <CircularProgress size={32} sx={{ color: "#10b981" }} />
            <Typography variant="body2" color="textSecondary">{t("AI is analyzing the organizational footprint...")}</Typography>
          </Box>
        ) : (!ai_insight?.insights?.length && !ai_insight?.env_hotspot?.length) ? (
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t("No insights generated yet. Click \"Generate Insights\" above to analyze the organizational footprint.")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {ai_insight?.insights?.map((insightText, index) => {
              let title = `${t("Insight")} ${index + 1}`;
              let description = insightText;

              // Clean up newlines for the text block first
              let cleanText = insightText.trim();

              // 1. Try to match "**Heading:** Description", "**Heading** - Description", or "**Heading**\nDescription"
              const boldMatch = cleanText.match(/^\*\*([^*]+)\*\*(?:\s*[:-]\s*|\s*\n\s*|\s+)(.*)$/s);
              // 2. Try to match "Heading: Description" or "Heading:\nDescription" (without bold)
              const colonMatch = cleanText.match(/^([^:\n*]+):\s*(.*)$/s);

              if (boldMatch) {
                title = boldMatch[1].trim();
                description = boldMatch[2].trim() || cleanText;
              } else if (colonMatch && colonMatch[1].length < 60) {
                // Only accept colon match if the heading part is reasonably short (not a full sentence)
                title = colonMatch[1].trim();
                description = colonMatch[2].trim() || cleanText;
              } else {
                // Fallback: If no clear heading structure, see if the first sentence/line is short
                const firstLineBreak = cleanText.indexOf('\n');
                if (firstLineBreak > 0 && firstLineBreak < 60) {
                  title = cleanText.substring(0, firstLineBreak).trim();
                  description = cleanText.substring(firstLineBreak).trim();
                }
              }

              // Remove stray bold markers from title and description
              title = title.replace(/\*\*/g, '');
              description = description.replace(/\*\*/g, '');

              // If title started with "Insight X\n", clean it up
              if (title.toLowerCase().startsWith("insight ") && title.length < 15) {
                return null; // Skip redundant headers sometimes generated by AI
              }

              // Clean up "Insight X" from the beginning of the text
              const insightPrefixRegex = /^Insight\s+\d+\s*\n*/i;
              description = description.replace(insightPrefixRegex, '');
              if (insightPrefixRegex.test(title)) {
                // AI just output "Insight X" as the bold header, so we try pulling the next line
                const nextLineBreak = description.indexOf('\n');
                if (nextLineBreak > 0 && nextLineBreak < 60) {
                  title = description.substring(0, nextLineBreak).trim();
                  description = description.substring(nextLineBreak).trim();
                } else {
                  title = "Intelligence Observation";
                }
              }

              return (
                <AIInsightCard
                  key={`insight-${index}`}
                  type="info"
                  title={title}
                  description={description}
                />
              );
            })}

            {ai_insight?.env_hotspot?.map((e, index) => (
              <AIInsightCard
                key={`hotspot-${index}`}
                type="warning"
                title={e.category}
                description={`Hotspot: ${e.hotspot} | Recommendation: ${e.recommendation}`}
              />
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default OrganizationInsight;
